import type { CompanyDetail } from "@kkp/api-client";
import { z } from "zod";
import { apiRequest } from "./client";
import { parseObject, wireInt } from "./parse";

/**
 * Boundary schema for the single company-detail aggregate. The four money fields
 * (perBatchLimitIdr, perTransactionFeeIdr, totalTransactionsAmountIdr,
 * totalFeesIdr) are MoneyIdr *strings* on the wire and stay strings end-to-end;
 * the dashboard's settled-volume card and the Company detail page parse them only
 * at the display formatter (which guards non-finite — the RpNaN class of bug).
 * Non-strict on purpose so any extra server-only fields are tolerated. The
 * inferred type matches the shared `CompanyDetail` (`string` money), enforced by
 * `getCompanyDetail`'s `Promise<CompanyDetail>` return annotation below.
 */
export const CompanyDetailSchema = z.object({
  companyId: z.string(),
  companyLegalName: z.string(),
  backOfficeId: z.string(),
  status: z.enum(["Active", "Inactive"]),
  onboardedBy: z.string(),
  onboardedAt: z.string(),
  taxId: z.string(),
  legalAddress: z.string(),
  sourceBankCode: z.string(),
  sourceBankAccount: z.string(),
  perBatchLimitIdr: z.string(),
  perTransactionFeeIdr: z.string(),
  picFullname: z.string(),
  picPhonenumber: z.string(),
  picEmail: z.string(),
  reasonDescription: z.string(),
  totalRecipient: wireInt,
  totalTransactions: wireInt,
  totalTransactionsAmountIdr: z.string(),
  totalFeesIdr: z.string(),
  picStatus: z.enum(["pending", "active", "disabled"]).nullable(),
}) satisfies z.ZodType<CompanyDetail>;

/**
 * Fetch a single company detail aggregate, validating money as a string at the
 * boundary. Throws an `ApiError` 502 on a malformed payload.
 */
export async function getCompanyDetail(id: string): Promise<CompanyDetail> {
  const data = await apiRequest<unknown>({
    path: `/company/${id}`,
    method: "GET",
  });
  return parseObject(CompanyDetailSchema, data, "company detail");
}
